import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { EMAIL, TRADES, HERO_IMG } from "@/lib/data";
import { EASE, FadeUp, MaskLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import TradeCard from "@/components/TradeCard";
import BriefBuilder from "@/components/BriefBuilder";

const HERO_LINES = ["Four trades,", "one standard."];

const STEPS = [
    {
        n: "01",
        title: "Survey & scope",
        desc: "We visit, assess and document the job — every intervention starts with a clear, written scope.",
    },
    {
        n: "02",
        title: "Install & certify",
        desc: "Work is executed to code, with the right materials and the proper documentation at sign-off.",
    },
    {
        n: "03",
        title: "Maintain & repair",
        desc: "We stay available for maintenance and fast repair — of everything we install, and what others have.",
    },
];

function Hero() {
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 180]);
    const dim = useTransform(scrollY, [0, 600], [1, 0.35]);

    return (
        <section className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
            <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
                <motion.img
                    src={HERO_IMG}
                    alt=""
                    style={{ opacity: dim }}
                    className="card-img h-[115%] w-full object-cover"
                    initial={{ scale: 1.12 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.2, ease: EASE }}
                />
                <div className="absolute inset-0 bg-[#0B0F17]/70" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse 70% 55% at 28% 30%, rgba(245,158,11,0.16), transparent 62%)",
                    }}
                />
                <div className="tech-grid absolute inset-0" />
            </motion.div>

            <div className="container-x relative pb-16 pt-40 md:pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                    className="eyebrow"
                >
                    Services &amp; Prestations Ltd — Multi-discipline technical contracting
                </motion.p>

                <h1 className="mt-6 font-display text-[13vw] font-extrabold leading-[0.98] tracking-[-0.02em] text-white sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
                    {HERO_LINES.map((line, i) => (
                        <MaskLine key={line} delay={0.3 + i * 0.16} className="max-w-5xl">
                            {i === 1 ? (
                                <span>
                                    one{" "}
                                    <span className="text-amber-400">standard.</span>
                                </span>
                            ) : (
                                line
                            )}
                        </MaskLine>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
                >
                    Electrical installation, plumbing, heat &amp; air-conditioning,
                    security systems, and appliance &amp; garden equipment repair —
                    engineered end to end under one accountable roof.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        data-testid="hero-get-started-button"
                        to="/contact"
                        className="btn-primary"
                    >
                        Request a quote
                        <ArrowUpRight size={16} />
                    </Link>
                    <Link
                        data-testid="hero-services-link"
                        to="/services"
                        className="btn-ghost"
                    >
                        Explore the trades
                        <ArrowDown size={16} />
                    </Link>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="relative border-t border-white/10 bg-[#0B0F17]/60 backdrop-blur-sm"
            >
                <div className="container-x grid grid-cols-2 gap-y-3 py-5 md:grid-cols-4">
                    {TRADES.map((t) => (
                        <div key={t.code} className="flex items-center gap-3">
                            <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ background: t.accent }}
                            />
                            <span className="font-mono text-[11px] tracking-[0.16em] text-slate-400">
                                {t.code}
                            </span>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}

function TradesSection() {
    return (
        <section className="py-24 md:py-32">
            <div className="container-x">
                <FadeUp>
                    <p className="eyebrow">01 — What we do</p>
                    <div className="mt-5 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                            Four regulated trades, one accountable partner.
                        </h2>
                        <Link
                            data-testid="trades-view-all-link"
                            to="/services"
                            className="group flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-amber-400"
                        >
                            View all services
                            <ArrowUpRight
                                size={14}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </Link>
                    </div>
                </FadeUp>

                <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
                    <TradeCard trade={TRADES[0]} className="md:col-span-7" />
                    <TradeCard trade={TRADES[1]} className="md:col-span-5" />
                    <TradeCard trade={TRADES[2]} className="md:col-span-5" />
                    <TradeCard trade={TRADES[3]} className="md:col-span-7" />
                </div>
            </div>
        </section>
    );
}

function MethodSection() {
    return (
        <section className="border-y border-white/10 bg-panel/30 py-24 md:py-32">
            <div className="container-x">
                <FadeUp>
                    <p className="eyebrow">02 — How we work</p>
                    <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        A method, not a guess.
                    </h2>
                </FadeUp>
                <div className="mt-14">
                    {STEPS.map((s, i) => (
                        <FadeUp key={s.n} delay={i * 0.08}>
                            <div className="group grid grid-cols-12 items-baseline gap-4 border-t border-white/10 py-8 transition-colors duration-300 last:border-b hover:bg-white/[0.02] md:items-center">
                                <span className="col-span-2 font-mono text-sm text-amber-400 md:col-span-1">
                                    {s.n}
                                </span>
                                <h3 className="col-span-10 font-display text-xl font-semibold tracking-tight text-white md:col-span-4 md:text-2xl">
                                    {s.title}
                                </h3>
                                <p className="col-span-12 col-start-3 text-sm leading-relaxed text-slate-400 md:col-span-6 md:col-start-6 md:text-base">
                                    {s.desc}
                                </p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BriefSection() {
    return (
        <section className="py-24 md:py-32">
            <div className="container-x">
                <FadeUp>
                    <p className="eyebrow">03 — Build your brief</p>
                    <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Three clicks to a quote request.
                    </h2>
                    <p className="mt-4 max-w-xl text-base text-slate-400">
                        Pick a trade, a scope and an urgency — we turn it into a
                        ready-made email to{" "}
                        <span className="text-slate-200">{EMAIL}</span>.
                    </p>
                </FadeUp>
                <div className="mt-12">
                    <BriefBuilder />
                </div>
            </div>
        </section>
    );
}

function CtaBand() {
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            toast.success("Email copied to clipboard");
        } catch {
            toast.error("Could not copy — the address is david@servicesprestations.com");
        }
    };

    return (
        <section className="border-t border-white/10 py-24 md:py-32">
            <div className="container-x">
                <FadeUp>
                    <p className="eyebrow">Ready when you are</p>
                    <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                        Let&apos;s get to work.
                    </h2>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link data-testid="home-cta-contact-link" to="/contact" className="btn-primary">
                            Request a quote
                            <ArrowUpRight size={16} />
                        </Link>
                        <button
                            data-testid="home-copy-email-button"
                            onClick={copy}
                            className="btn-ghost"
                        >
                            <Copy size={15} />
                            {EMAIL}
                        </button>
                    </div>
                </FadeUp>
            </div>
        </section>
  );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee
                items={TRADES.map((t) => `${t.code} — ${t.title}`)}
            />
            <TradesSection />
            <MethodSection />
            <BriefSection />
            <CtaBand />
        </>
    );
}
